<template>
  <div class="package-upload">
    <div class="page-header">
      <van-icon name="arrow-left" size="24" class="back-icon" @click="goBack" />
      <h2 class="page-title">{{ getPageTitle }}</h2>
    </div>

    <div class="upload-container">
      <div class="upload-area">
        <div class="version-form">
          <h3 class="form-title">版本信息</h3>
          <van-field
            v-model="versionInfo.version"
            placeholder="请输入版本号，如：1.0.0"
            :rules="[{ required: true, message: '请输入版本号' }]"
            class="version-input"
          >
            <template #label>
              <span class="required-label">版本号</span>
            </template>
          </van-field>
          <van-field
            v-model="versionInfo.description"
            type="textarea"
            rows="4"
            maxlength="500"
            show-word-limit
            placeholder="请输入本次更新的主要内容，如：新增功能、修复问题等"
            :rules="[{ required: true, message: '请输入更新说明' }]"
            class="description-input"
          >
            <template #label>
              <span class="required-label">更新说明</span>
            </template>
          </van-field>
        </div>

        <van-uploader
          v-model="fileList"
          :max-count="1"
          :before-read="beforeUpload"
          :after-read="handleAfterRead"
          @delete="handleDelete"
          :accept="allowedExtensionsList.join(',')"
        >
          <div class="upload-button" :class="{ uploading: uploading }">
            <van-icon :name="uploading ? 'upgrade' : 'plus'" size="32" />
            <p class="upload-text">{{ uploading ? '正在上传...' : '点击上传安装包' }}</p>
            <p class="upload-hint">支持 {{ allowedExtensionsList.join('、') }} 格式，大小不超过 50MB</p>
          </div>
        </van-uploader>

        <div v-if="fileList.length > 0" class="selected-file">
          <van-icon name="description" />
          <span class="file-name">{{ fileList[0].name }}</span>
          <span class="file-size">{{ formatFileSize(fileList[0].size) }}</span>
        </div>
      </div>

      <div class="current-version" v-loading="loading">
        <template v-if="currentPackage">
          <div class="version-header">
            <van-icon name="checked" size="20" />
            <h3>当前版本</h3>
          </div>
          <div class="version-info">
            <div class="info-item">
              <span class="label">文件名</span>
              <span class="value">{{ currentPackage.fileName }}</span>
            </div>
            <div class="info-item">
              <span class="label">版本号</span>
              <span class="value">{{ currentPackage.version }}</span>
            </div>
            <div class="info-item">
              <span class="label">上传时间</span>
              <span class="value">{{ currentPackage.uploadTime }}</span>
            </div>
            <div class="info-item">
              <span class="label">文件大小</span>
              <span class="value">{{ currentPackage.size }}</span>
            </div>
            <div class="info-item description">
              <span class="label">更新说明</span>
              <div class="value description-content">{{ currentPackage.description }}</div>
            </div>
          </div>
        </template>
        <template v-else-if="!loading">
          <div class="empty-state">
            <van-empty description="暂无版本信息" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { getPackageInfo, uploadPackage, getPackageTypes } from '@/api/package'

export default {
  name: 'PackageUpload',
  data() {
    return {
      fileList: [],
      currentPackage: null,
      // 后端类型元数据映射（value -> meta）
      typeMetaMap: {},
      versionInfo: {
        version: '',
        description: ''
      },
      uploading: false,
      loading: false
    }
  },
  computed: {
    packageType() {
      // 路由参数即后端类型 value，直接使用
      const type = this.$route.params.type
      console.log('当前包类型:', type) // 添加日志
      return type
    },
    getPageTitle() {
      const meta = this.typeMetaMap[this.packageType]
      const title = meta && meta.name
      return title ? `${title}上传` : '安装包上传'
    },
    allowedExtensionsList() {
      const meta = this.typeMetaMap[this.packageType]
      const list = (meta && meta.allowed_extensions) || []
      // input accept 需要 .ext 形式
      return list.map(ext => (ext && ext.startsWith('.') ? ext : `.${ext}`))
    },
    maxSize() {
      const meta = this.typeMetaMap[this.packageType]
      return (meta && meta.max_file_size) || 50 * 1024 * 1024
    },
    maxSizeMB() {
      const meta = this.typeMetaMap[this.packageType]
      return (meta && meta.max_file_size_mb) || Math.round(this.maxSize / 1024 / 1024)
    }
  },
  created() {
    // 组件创建时立即获取当前版本信息
    console.log('组件创建，先拉取类型元数据并获取当前版本信息') // 添加日志
    this.fetchTypeMeta().then(() => {
      this.getCurrentPackage()
    })
  },
  watch: {
    // 监听路由参数变化，重新获取当前版本信息
    packageType: {
      immediate: true,
      handler(newType) {
        console.log('包类型变化，重新获取版本信息:', newType) // 添加日志
        if (newType) {
          // 类型变化时也刷新元数据，以防后端配置更新
          this.fetchTypeMeta().finally(() => {
            this.getCurrentPackage()
          })
        }
      }
    }
  },
  methods: {
    async fetchTypeMeta() {
      try {
        const res = await getPackageTypes()
        console.log('获取类型元数据响应:', res)
        const list = Array.isArray(res && res.data) ? res.data : []
        const map = {}
        list.forEach(item => {
          if (item && item.value) {
            map[item.value] = item
          }
        })
        this.typeMetaMap = map
      } catch (e) {
        console.error('获取类型元数据失败:', e)
        // 不中断流程，保持本地回退
      }
    },
    goBack() {
      this.$router.push('/package')
    },
    formatFileSize(size) {
      const units = ['B', 'KB', 'MB', 'GB']
      let index = 0
      let fileSize = size

      while (fileSize >= 1024 && index < units.length - 1) {
        fileSize /= 1024
        index++
      }

      return `${fileSize.toFixed(2)} ${units[index]}`
    },
    getFileExtension(filename) {
      return filename.toLowerCase().substring(filename.lastIndexOf('.'))
    },
    beforeUpload(file) {
      // 检查版本信息是否已填写
      if (!this.versionInfo.version || !this.versionInfo.description) {
        this.$toast('请先填写完整的版本信息')
        return false
      }

      // 检查版本号格式
      const versionPattern = /^\d+\.\d+\.\d+$/
      if (!versionPattern.test(this.versionInfo.version)) {
        this.$toast('请输入正确的版本号格式（如：1.0.0）')
        return false
      }

      // 检查文件类型
      const fileExtension = this.getFileExtension(file.name)
      const isValidType = this.allowedExtensionsList.includes(fileExtension)
      if (!isValidType) {
        this.$toast(`请上传正确的文件格式（${this.allowedExtensionsList.join('、')}）`)
        return false
      }

      // 检查文件大小
      const isValidSize = file.size <= this.maxSize
      if (!isValidSize) {
        this.$toast(`文件大小不能超过 ${this.maxSize / 1024 / 1024}MB`)
        return false
      }

      return true
    },
    handleDelete() {
      this.fileList = []
    },
    async handleAfterRead(file) {
      if (!file) return

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('type', this.packageType)
        formData.append('version', this.versionInfo.version)
        formData.append('file', file.file)
        formData.append('description', this.versionInfo.description)

        const response = await uploadPackage(formData)
        if (response && (response.success === true || response.code === 200)) {
          this.$toast.success('上传成功')
          this.handleDelete()
          this.versionInfo = {
            version: '',
            description: ''
          }
          // 刷新当前版本信息
          this.getCurrentPackage()
        } else {
          this.$toast.fail((response && (response.message || response.msg)) || '上传失败')
          // 上传失败时清除文件
          this.handleDelete()
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$toast.fail('上传失败，请重试')
        // 上传失败时清除文件
        this.handleDelete()
      } finally {
        this.uploading = false
      }
    },
    async getCurrentPackage() {
      if (!this.packageType) {
        console.log('未获取到包类型，跳过获取版本信息') // 添加日志
        return
      }

      console.log('开始获取版本信息，类型:', this.packageType) // 添加日志
      this.loading = true
      this.currentPackage = null
      try {
        const response = await getPackageInfo(this.packageType)
        console.log('获取版本信息响应:', response) // 添加日志
        if (response && response.data) {
          this.currentPackage = {
            fileName: response.data.package_name,
            version: response.data.version,
            uploadTime: response.data.upload_time,
            size: this.formatFileSize(response.data.file_size),
            description: response.data.description
          }
        }
      } catch (error) {
        console.error('获取当前版本信息失败:', error)
        this.$toast('获取当前版本信息失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.package-upload {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 40px;

  .back-icon {
    color: #2c3e50;
    cursor: pointer;
    padding: 8px;
    margin-right: 8px;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(25, 137, 250, 0.1);
      color: #1989fa;
    }
  }
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.upload-container {
  max-width: 800px;
  margin: 0 auto;
}

.upload-area {
  background: #ffffff;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
}

.version-form {
  margin-bottom: 30px;
  text-align: left;
  padding: 24px;
  background: #f7f8fa;
  border-radius: 12px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);

  .form-title {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
    margin: 0 0 20px 0;
  }

  .required-label {
    position: relative;
    color: #646566;

    &::before {
      content: '*';
      color: #ee0a24;
      margin-right: 4px;
    }
  }

  :deep(.van-field) {
    margin-bottom: 16px;
    background: transparent;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(.version-input) {
    .van-field__control {
      background: #ffffff;
      border-radius: 8px;
      padding: 12px;
      height: 44px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
  }

  :deep(.description-input) {
    .van-field__control {
      background: #ffffff;
      border-radius: 8px;
      padding: 12px;
      min-height: 120px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      font-size: 14px;
      line-height: 1.6;
    }
  }

  :deep(.van-field__word-limit) {
    margin-top: 8px;
    color: #909399;
  }
}

.upload-button {
  width: 100%;
  height: 200px;
  border: 2px dashed #dcdee0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(to bottom, #ffffff, #f7f8fa);

  &:hover {
    border-color: #1989fa;
    background: linear-gradient(to bottom, #ffffff, #f0f9ff);

    .van-icon {
      color: #1989fa;
      transform: scale(1.1);
    }
  }

  &.uploading {
    border-color: #1989fa;
    background: linear-gradient(to bottom, #ffffff, #f0f9ff);

    .van-icon {
      color: #1989fa;
      animation: rotate 1s linear infinite;
    }
  }

  .van-icon {
    color: #969799;
    margin-bottom: 12px;
    transition: all 0.3s ease;
  }

  .upload-text {
    font-size: 16px;
    color: #323233;
    margin: 8px 0;
    font-weight: 500;
  }

  .upload-hint {
    font-size: 14px;
    color: #969799;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.selected-file {
  margin-top: 20px;
  padding: 16px;
  background: #f0f9ff;
  border-radius: 8px;
  display: flex;
  align-items: center;

  .van-icon {
    color: #1989fa;
    font-size: 20px;
    margin-right: 12px;
  }

  .file-name {
    flex: 1;
    color: #2c3e50;
    font-weight: 500;
    margin-right: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-size {
    color: #909399;
    font-size: 14px;
  }
}

.current-version {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  position: relative;
  min-height: 200px;

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
  }

  .version-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .van-icon {
      color: #67c23a;
      margin-right: 8px;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .version-info {
    background: #f7f8fa;
    border-radius: 8px;
    padding: 16px;
  }

  .info-item {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:first-child {
      padding-top: 0;
    }

    &.description {
      align-items: flex-start;
    }

    .label {
      width: 80px;
      color: #646566;
      font-size: 14px;
    }

    .value {
      flex: 1;
      color: #323233;
      font-size: 14px;

      &.description-content {
        white-space: pre-line;
        line-height: 1.6;
        background: #ffffff;
        padding: 12px;
        border-radius: 6px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .package-upload {
    padding: 16px;
  }

  .upload-area {
    padding: 20px;
  }

  .upload-button {
    height: 160px;
  }

  .version-form {
    padding: 16px;
  }

  .current-version {
    padding: 20px;
  }
}
</style>
